// apps/client/src/components/loans/LoanPortfolioStats.tsx
// Stat cards for the Loan Portfolio page
// Updated 2026-02-01: Changed "Undischarged" to "Active" for consistency with Settlement Age
//
// Metrics displayed (5 cards):
// - Line of Trail: Loans with commission (Tier 1 - matches Dashboard LOT)
// - Active: Loans without discharge event (Tier 2 - Asset context)
// - Discharged: Loans with discharge event (Tier 2 - Asset context)
// - Total Ever: All loans ever recorded (Tier 3 - Historical)
// - Avg Active Age: Average age of active loans

import React, { useMemo } from 'react';
import StatCard from '../common/StatCard';
import { useLoanPortfolioStats } from '../../hooks/useLoanPortfolioStats';
import { useRolling3RunWindow } from '../../hooks/useRolling3RunWindow';
import { useAuth } from '../../context/AuthContext';
import { getMonthRangeFromPeriod } from '../../utils/periodUtils';

interface LoanPortfolioStatsProps {
  filters?: {
    brokerName?: string;
    lender?: string;
  };
  entityCode?: string;
}

/**
 * Format age in months to a readable string.
 * Uses months if < 24, otherwise shows years.
 */
function formatAge(months: number): string {
  if (!months || months === 0) return '0 months';
  if (months < 24) {
    return `${Math.round(months)} months`;
  }
  return `${(months / 12).toFixed(1)} years`;
}

export function LoanPortfolioStats({ filters, entityCode }: LoanPortfolioStatsProps) {
  const { stats, loading } = useLoanPortfolioStats(filters);
  const { companyId } = useAuth();

  // LOT: Use EXACT same hook + params as DashboardPage.tsx (line 99-107)
  // This guarantees the LOT stat card matches the main dashboard
  const latestSnapshotRange = useMemo(() => {
    return getMonthRangeFromPeriod('1Y', new Date());
  }, []);

  const {
    totalLineOfTrail,
    loading: loadingLot,
  } = useRolling3RunWindow({
    companyId: companyId || '',
    officeId: undefined,
    templateId: 'all',
    entityCode,
    monthRange: {
      startMonth: latestSnapshotRange.startMonth,
      endMonth: latestSnapshotRange.endMonth,
    },
  });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
      {/* Tier 1: Line of Trail (Primary Metric - SAME source as Dashboard) */}
      <StatCard
        title="Line of Trail"
        value={totalLineOfTrail?.toLocaleString() || '0'}
        subtitle="Loans with commission"
        gradientColor="#8b5cf6"
        isUpdating={loadingLot}
      />
      {/* Tier 2: Portfolio Metrics */}
      <StatCard
        title="Active"
        value={stats?.undischargedLoans?.toLocaleString() || stats?.activeLoans?.toLocaleString() || '0'}
        subtitle="Not yet discharged"
        gradientColor="#22c55e"
        isUpdating={loading}
      />
      <StatCard
        title="Discharged"
        value={stats?.dischargedLoans?.toLocaleString() || '0'}
        subtitle="No longer active"
        gradientColor="#ef4444"
        isUpdating={loading}
      />
      {/* Tier 3: Historical */}
      <StatCard
        title="Total Ever"
        value={stats?.totalLoansEver?.toLocaleString() || stats?.totalLoans?.toLocaleString() || '0'}
        subtitle="All loans recorded"
        gradientColor="#3b82f6"
        isUpdating={loading}
      />
      {/* Age Metrics */}
      <StatCard
        title="Avg Active Age"
        value={formatAge(stats?.avgActiveAgeMonths || 0)}
        subtitle="Since settlement"
        gradientColor="#f97316"
        isUpdating={loading}
      />
    </div>
  );
}
